import { View, Text, styled } from 'tamagui'
import { Hash } from '@tamagui/lucide-icons'
import { Avatar } from '../../common'
import { users } from '@/mocks'
import { useServerStore, useAppStore } from '@/store'
import type { User, Channel } from '@/types'

const Dropdown = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingVertical: 4,
  maxHeight: 240,
  overflow: 'hidden',
})

const ResultItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 36,
  paddingHorizontal: 12,
  gap: 8,
  cursor: 'pointer',
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

const DisplayName = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$surfaceForeground',
  fontWeight: '500',
})

const Username = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  color: '$sidebarMuted',
})

const ChannelName = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$surfaceForeground',
})

interface MentionAutocompleteProps {
  type: '@' | '#'
  query: string
  selectedIndex: number
  onSelect: (value: string, display: string) => void
}

export function MentionAutocomplete({ type, query, selectedIndex, onSelect }: MentionAutocompleteProps) {
  const activeServerId = useAppStore((s) => s.activeServerId)
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)

  if (type === '@') {
    const q = query.toLowerCase()
    const matches: User[] = Object.values(users)
      .filter((u) => u.displayName.toLowerCase().includes(q) || u.username.toLowerCase().includes(q))
      .slice(0, 5)

    if (matches.length === 0) return null

    return (
      <Dropdown>
        {matches.map((user, i) => (
          <ResultItem
            key={user.id}
            backgroundColor={i === selectedIndex ? '$sidebarHover' : 'transparent'}
            onPress={() => onSelect(user.username, user.displayName)}
            onMouseEnter={(e: any) => {
              // Visual hover handled by selectedIndex from parent
            }}
          >
            <Avatar
              name={user.displayName}
              color={user.roleColor || '#2A3142'}
              size="sm"
              showStatus={false}
            />
            <DisplayName color={user.roleColor || '$surfaceForeground'}>
              {user.displayName}
            </DisplayName>
            <Username>@{user.username}</Username>
          </ResultItem>
        ))}
      </Dropdown>
    )
  }

  // Channel mode
  if (!server) return null
  const q = query.toLowerCase()
  const matches: Channel[] = server.channels
    .filter((c) => c.type === 'text' && c.name.toLowerCase().includes(q))
    .slice(0, 5)

  if (matches.length === 0) return null

  return (
    <Dropdown>
      {matches.map((ch, i) => (
        <ResultItem
          key={ch.id}
          backgroundColor={i === selectedIndex ? '$sidebarHover' : 'transparent'}
          onPress={() => onSelect(ch.name, ch.name)}
        >
          <Hash size={16} color="$color8" />
          <ChannelName>{ch.name}</ChannelName>
        </ResultItem>
      ))}
    </Dropdown>
  )
}

export function getAutocompleteMatches(type: '@' | '#', query: string, server?: { channels: Channel[] }): number {
  if (type === '@') {
    const q = query.toLowerCase()
    return Object.values(users).filter((u) =>
      u.displayName.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)
    ).slice(0, 5).length
  }
  if (!server) return 0
  const q = query.toLowerCase()
  return server.channels.filter((c) => c.type === 'text' && c.name.toLowerCase().includes(q)).slice(0, 5).length
}
